/**
 * Media service — V4-21/V4-22 (20-05-2026).
 *
 * Wrapper autour de `/api/v1/media/upload` (POST multipart) qui persiste un
 * fichier dans MinIO et retourne :
 *   - `url` : URL PROXY backend (`/api/v1/media/serve/{object_name}`) — passe
 *     par l'API avec auth JWT + validation ownership. **MinIO n'est jamais
 *     exposé publiquement** (V4-22).
 *   - `object_name` : `{user_id}/{uuid}.{ext}` — utilisé par
 *     `/generation/from-visual` qui en extrait le user_id pour ownership check.
 *
 * Pourquoi ce service ?
 * ─────────────────────
 * Avant V4-21, `handleImageUpload` créait une `URL.createObjectURL(file)`
 * locale (blob: URL) qui :
 *   1. N'uploadait JAMAIS le fichier côté serveur (perdu au reload).
 *   2. Faisait échouer `/generation/from-visual` (403 "owned by 'http:'").
 *
 * V4-22 (sécurité) : on n'expose plus de presigned MinIO URL au browser. Le
 * frontend reçoit une URL proxy backend que `AuthenticatedImage` consomme via
 * fetch + Bearer token. Conséquence prod : MinIO peut rester sur réseau privé
 * (pas de port 9000 exposé en public).
 *
 * Timeout : 60s. Les uploads >5 MB sont rejetés par le backend.
 */

import { apiClient } from './apiClient';
import Config from '../config/config';

export interface UploadedMedia {
  /** URL proxy (`/api/v1/media/serve/...`) — utilisable directement par AuthenticatedImage. */
  url: string;
  /** `{user_id}/{uuid}.{ext}` — à passer aux endpoints qui font ownership check (from-visual). */
  object_name: string;
  filename: string;
  content_type: string;
}

interface UploadResponse {
  filename: string;
  object_name: string;
  url: string;
  content_type: string;
}

export const mediaService = {
  async uploadFile(file: File): Promise<UploadedMedia> {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post<UploadResponse>(
      '/media/upload',
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      },
    );
    const data = response.data;
    // V4-22 — Construire l'URL proxy absolue à partir de `object_name` côté client.
    // `Config.apiUrl` contient déjà `/api/v1` (ex: `http://localhost:8000/api/v1`).
    // Pattern identique à generationService.ts:233 pour les images générées.
    const apiUrl = Config.getInstance().apiUrl.replace(/\/$/, '');
    const proxyUrl = `${apiUrl}/media/serve/${data.object_name}`;
    return {
      url: proxyUrl,
      object_name: data.object_name,
      filename: data.filename,
      content_type: data.content_type,
    };
  },
};
